import { NavLink, useNavigate } from 'react-router-dom';

export default function BottomNavBar({ user, basketCount, onToggleMenu, onShowChat, onSwitchRole, onShowLogin }) {
    const navigate = useNavigate();

    // Active style for NavLink
    const activeLinkStyle = {
        color: '#2563EB' // equivalent to text-blue-600
    };

    // Get navigation items based on user role and login status
    const getNavigationItems = () => {
        const isLoggedIn = !!user;
        const userRole = user?.activeRole;
        const canSwitchRoles = user && user.roles && user.roles.length > 1;
        const roleToSwitchTo = canSwitchRoles ? user.roles.find(r => r !== userRole) : null;

        let navigationItems = [];

        // Add Home button for buyers only (not sellers or admins)
        if (userRole === 'buyer') {
            navigationItems.push({
                to: "/app-home",
                icon: "fa-home",
                label: "Home"
            });
        }

        // Role-specific navigation
        if (userRole === 'buyer') {
            // Buyer navigation - remove basket to make room for menu, add saved items
            navigationItems.push(
                {
                    to: "/categories",
                    icon: "fa-th-large",
                    label: "Categories"
                },
                {
                    to: "/saved-items",
                    icon: "fa-heart",
                    label: "Saved"
                }
            );
        } else if (userRole === 'seller') {
            // Seller navigation - no home, dashboard is home
            navigationItems.push(
                {
                    type: "button",
                    icon: "fa-list",
                    label: "Listings",
                    onClick: () => {
                        // Navigate to dashboard with anchor
                        navigate('/dashboard#my-listings');
                        
                        // Scroll to section after navigation
                        setTimeout(() => {
                            const element = document.getElementById('my-listings');
                            if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        }, 200);
                    }
                },
                {
                    type: "button",
                    icon: "fa-shopping-bag",
                    label: "Orders",
                    onClick: () => {
                        // Navigate to dashboard with anchor
                        navigate('/dashboard#order-fulfillment');
                        
                        // Scroll to section after navigation
                        setTimeout(() => {
                            const element = document.getElementById('order-fulfillment');
                            if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        }, 200);
                    }
                },
                {
                    type: "button",
                    icon: "fa-plus-circle",
                    label: "Add",
                    onClick: () => {
                        // Trigger custom event to open AddItemWizard
                        const event = new CustomEvent('openAddItemWizard');
                        window.dispatchEvent(event);
                    }
                }
            );
        } else if (userRole === 'admin') {
            // Admin navigation - all admin pages
            navigationItems.push(
                {
                    to: "/supplier-analytics",
                    icon: "fa-chart-bar",
                    label: "Analytics"
                },
                {
                    to: "/admin/kg-analytics",
                    icon: "fa-chart-line",
                    label: "KG Analytics"
                },
                {
                    to: "/admin/kp-insurance",
                    icon: "fa-shield-alt",
                    label: "Insurance"
                },
                {
                    to: "/admin/test-watermark",
                    icon: "fa-magic",
                    label: "Watermark"
                }
            );
        }

        // Add hamburger menu for all roles
        navigationItems.push({
            type: "button",
            icon: "fa-bars",
            label: "Menu",
            onClick: onToggleMenu
        });

        // Profile/Login button (no role switcher - it's in top nav)
        if (isLoggedIn) {
            // Dashboard button instead of Profile
            navigationItems.push({
                to: "/dashboard",
                icon: "fa-tachometer-alt",
                label: "Dashboard"
            });
        } else {
            // Login button for guests
            navigationItems.push({
                type: "button",
                icon: "fa-sign-in-alt",
                label: "Login",
                onClick: onShowLogin
            });
        }

        return navigationItems;
    };

    const navigationItems = getNavigationItems();

    return (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-t-lg z-40 flex justify-around items-center h-20">
            {navigationItems.map((item, index) => {
                if (item.type === 'button') {
                    return (
                        <button 
                            key={index} 
                            onClick={item.onClick} 
                            className={`flex flex-col items-center justify-center transition-colors w-1/5 relative ${
                                item.isRoleSwitcher 
                                    ? 'text-blue-600 hover:text-blue-700 bg-blue-50 rounded-lg mx-1' 
                                    : 'text-gray-500 hover:text-blue-600'
                            }`}
                        >
                            <i className={`fas ${item.icon} text-xl`}></i>
                            <span className="text-xs mt-1 font-medium">{item.label}</span>
                        </button>
                    );
                }
                
                return (
                    <NavLink 
                        key={index}
                        to={item.to} 
                        style={({ isActive }) => isActive ? activeLinkStyle : undefined} 
                        className="relative flex flex-col items-center justify-center text-gray-500 hover:text-blue-600 transition-colors w-1/5"
                    >
                        <i className={`fas ${item.icon} text-xl`}></i>
                        <span className="text-xs mt-1">{item.label}</span>
                        {item.badge && (
                            <span className="absolute -top-1 right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center text-[10px]">
                                {item.badge}
                            </span>
                        )}
                    </NavLink>
                );
            })}
        </nav>
    );
}
